import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import type { PatchConversationsRequestBody } from "@app/types/api/assistant/conversation/types";
import type { AgentReplyMode } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useState } from "react";
import { useConversation } from "./useConversation";
import { useConversations } from "./useConversations";

interface UseConversationAgentReplyModeProps {
  owner: LightWorkspaceType;
  conversationId: string | null;
}

export function useConversationAgentReplyMode({
  owner,
  conversationId,
}: UseConversationAgentReplyModeProps) {
  const sendNotification = useSendNotification();
  const { mutateConversation } = useConversation({
    conversationId,
    workspaceId: owner.sId,
    options: { disabled: true },
  });
  const { mutateConversations } = useConversations({
    workspaceId: owner.sId,
    options: { disabled: true },
  });
  const [isUpdatingAgentReplyMode, setIsUpdatingAgentReplyMode] =
    useState(false);

  const updateAgentReplyMode = useCallback(
    async (agentReplyMode: AgentReplyMode): Promise<boolean> => {
      if (!conversationId || isUpdatingAgentReplyMode) {
        return false;
      }

      setIsUpdatingAgentReplyMode(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/assistant/conversations/${conversationId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              agentReplyMode,
            } satisfies PatchConversationsRequestBody),
          }
        );

        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Failed to update who replies",
          });
          return false;
        }

        const metadataPatch = { agentReplyMode };
        void mutateConversation(
          (previousData) =>
            previousData?.conversation
              ? {
                  ...previousData,
                  conversation: {
                    ...previousData.conversation,
                    metadata: {
                      ...previousData.conversation.metadata,
                      ...metadataPatch,
                    },
                  },
                }
              : previousData,
          { revalidate: false }
        );
        void mutateConversations(
          (previousConversations) =>
            previousConversations?.map((conversation) =>
              conversation.sId === conversationId
                ? {
                    ...conversation,
                    metadata: {
                      ...conversation.metadata,
                      ...metadataPatch,
                    },
                  }
                : conversation
            ),
          { revalidate: false }
        );
        sendNotification({
          type: "success",
          title:
            agentReplyMode === "tagged"
              ? "Agents reply only when tagged"
              : "Agents reply automatically",
        });
        return true;
      } finally {
        setIsUpdatingAgentReplyMode(false);
      }
    },
    [
      conversationId,
      isUpdatingAgentReplyMode,
      mutateConversation,
      mutateConversations,
      owner.sId,
      sendNotification,
    ]
  );

  return {
    isUpdatingAgentReplyMode,
    updateAgentReplyMode,
  };
}
